import { CloseIcon } from '@repo/atomic-components';
import {
  getLiveQuestionFailedMessage,
  getLiveQuestionStalledMessage,
  LIVE_QUESTIONS_PROCESSING_MESSAGE,
} from './constants';
import { LiveQuestionNotice } from './useLiveQuestionNotices';

type MedicalMatchProcessingBarProps = {
  /** True while the worker still has live questions to answer. */
  isProcessing: boolean;
  /** Questions that will never be answered — one notice each, named so the user knows which to ask again. */
  notices: LiveQuestionNotice[];
  onDismissNotice: (questionId: string) => void;
};

export function MedicalMatchProcessingBar({ isProcessing, notices, onDismissNotice }: MedicalMatchProcessingBarProps) {
  return (
    <>
      {isProcessing && (
        <div
          role="status"
          aria-live="polite"
          className="bg-fletchers-green-50 text-fletchers-green-600 mb-3 flex items-center gap-2 rounded-md px-3 py-2 text-[14px]"
        >
          <span
            aria-hidden
            className="border-fletchers-green-600 h-[14px] w-[14px] shrink-0 animate-spin rounded-full border-2 border-t-transparent"
          />
          <span>{LIVE_QUESTIONS_PROCESSING_MESSAGE}</span>
        </div>
      )}
      {notices.map((notice) => (
        <LiveQuestionNoticeCard key={notice.questionId} notice={notice} onDismiss={onDismissNotice} />
      ))}
    </>
  );
}

type LiveQuestionNoticeCardProps = {
  notice: LiveQuestionNotice;
  onDismiss: (questionId: string) => void;
};

function LiveQuestionNoticeCard({ notice, onDismiss }: LiveQuestionNoticeCardProps) {
  return (
    <div
      role="alert"
      className="bg-fletchers-yellow-500/10 text-fletchers-black mb-3 flex items-start gap-2 rounded-md p-3 text-[14px]"
    >
      <p className="min-w-0 flex-1 break-words">
        {notice.reason === 'FAILED'
          ? getLiveQuestionFailedMessage(notice.question)
          : getLiveQuestionStalledMessage(notice.question)}
      </p>
      {/* Dismissal is permanent — see useLiveQuestionNotices. Nothing else clears
          these, as a stalled question stays in the processing list for good. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(notice.questionId)}
        className="hover:bg-fletchers-yellow-500/20 focus-visible:outline-fletchers-yellow-500 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors focus-visible:outline-2"
      >
        <CloseIcon aria-hidden width={10} height={10} />
      </button>
    </div>
  );
}
